import React from 'react';
import { Activity, AlertTriangle, CheckCircle2, Play, RefreshCw, Search, Shield } from 'lucide-react';
import { openapiApi } from '@/config/api';
import { useNavigate } from 'react-router-dom';

function Kpi({ title, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value ?? '—'}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}

function StatusPill({ value }) {
  const tone = {
    Healthy: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Passed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Warning: 'bg-amber-50 text-amber-800 border-amber-200',
    Failed: 'bg-red-50 text-red-800 border-red-200',
    Blocked: 'bg-slate-100 text-slate-700 border-slate-300',
    Documented: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Undocumented: 'bg-red-50 text-red-800 border-red-200',
    'Documentation Incomplete': 'bg-amber-50 text-amber-800 border-amber-200',
    'Ready for Review': 'bg-sky-50 text-sky-800 border-sky-200',
    'Pending Verification': 'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-bold ${tone[value] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      {value || '—'}
    </span>
  );
}

export default function ApiQualityDashboard() {
  const navigate = useNavigate();
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [moduleFilter, setModuleFilter] = React.useState('ALL');
  const [methodFilter, setMethodFilter] = React.useState('ALL');
  const [statusFilter, setStatusFilter] = React.useState('ALL');
  const [page, setPage] = React.useState(1);
  const pageSize = 25;

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await openapiApi.get('/openapi/quality/latest');
      setReport(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Gagal memuat quality report');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runSuite = async () => {
    setRunning(true);
    setError('');
    try {
      await openapiApi.post('/openapi/quality/run', {});
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Gagal menjalankan suite');
    } finally {
      setRunning(false);
    }
  };

  const modules = React.useMemo(() => {
    const set = new Set((report?.endpoints || []).map((e) => e.module).filter(Boolean));
    return [...set].sort();
  }, [report]);

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (report?.endpoints || []).filter((e) => {
      if (moduleFilter !== 'ALL' && e.module !== moduleFilter) return false;
      if (methodFilter !== 'ALL' && e.method !== methodFilter) return false;
      if (statusFilter !== 'ALL') {
        const s = `${e.documentationStatus} ${e.contractStatus}`;
        if (statusFilter === 'Failed' && !(e.contractStatus === 'Failed' || e.documentationStatus === 'Undocumented')) return false;
        if (statusFilter === 'Passed' && e.contractStatus !== 'Passed') return false;
        if (statusFilter === 'Warning' && e.contractStatus !== 'Warning') return false;
        if (!['Failed', 'Passed', 'Warning'].includes(statusFilter) && !s.includes(statusFilter)) return false;
      }
      if (!needle) return true;
      return [e.method, e.path, e.module, e.operationId, e.documentationStatus].join(' ').toLowerCase().includes(needle);
    });
  }, [report, query, moduleFilter, methodFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  if (loading && !report) {
    return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Memuat API Quality report…</div>;
  }

  const kpi = report?.kpi || {};
  const gate = report?.qualityGate || {};

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">API Quality & Coverage</h2>
            <p className="mt-1 text-sm text-slate-600">
              Inventarisasi route vs OpenAPI, structural contract checks, breaking-change vs baseline, dan Gateway readiness.
              Metrik dihitung dari audit aktual — bukan angka dummy.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <StatusPill value={report?.overallStatus} />
              <span>Gate: {gate.status}</span>
              <span>Exec: {report?.executionId || '—'}</span>
              <span>{report?.generatedAt ? new Date(report.generatedAt).toLocaleString('id-ID') : ''}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button type="button" disabled={running} onClick={runSuite} className="inline-flex items-center gap-2 rounded-md bg-teal-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
              <Play className="h-4 w-4" /> {running ? 'Running…' : 'Run structural suite'}
            </button>
          </div>
        </div>
        {error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        <p className="mt-3 text-[11px] text-slate-400">
          Run suite hanya menjalankan predefined structural checks (bukan shell arbitrer, bukan mutation production).
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Kpi title="In-Scope APIs" value={kpi.totalInScopeApis} />
        <Kpi title="Documented" value={kpi.documentedApis} />
        <Kpi title="Coverage" value={report?.coverage?.coverageDisplay} hint={report?.coverage?.totalInScope === 0 ? 'N/A' : undefined} />
        <Kpi title="Passed" value={kpi.passedTests} />
        <Kpi title="Failed" value={kpi.failedTests} />
        <Kpi title="Blocked" value={kpi.blockedTests} />
        <Kpi title="Security fails" value={kpi.securityFailures} />
        <Kpi title="Breaking" value={kpi.breakingChanges ?? '—'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-bold"><Shield className="h-4 w-4" /> Quality gate</div>
          <StatusPill value={gate.status} />
          {(gate.fail || []).length > 0 && (
            <ul className="list-disc pl-5 text-xs text-red-700">
              {gate.fail.map((f) => <li key={f}>{f}</li>)}
            </ul>
          )}
          {(gate.blocked || []).length > 0 && (
            <ul className="list-disc pl-5 text-xs text-slate-600">
              {gate.blocked.slice(0, 5).map((f) => <li key={f}>{f}</li>)}
            </ul>
          )}
          {(gate.warning || []).slice(0, 5).map((w) => (
            <div key={w} className="text-xs text-amber-700 flex gap-1"><AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />{w}</div>
          ))}
          {!gate.fail?.length && !gate.warning?.length && gate.status === 'Passed' && (
            <div className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Required validations passed</div>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-bold"><Activity className="h-4 w-4" /> Gateway readiness</div>
          <StatusPill value={report?.gatewayReadiness?.readiness} />
          <p className="text-xs text-slate-500">{report?.gatewayReadiness?.note}</p>
          <ul className="space-y-1">
            {(report?.gatewayReadiness?.checks || []).map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span>{c.label}</span>
                <StatusPill value={c.status} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {(report?.breakingChanges?.changes || []).length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-2 text-sm font-bold">Breaking change detection</div>
          <div className="max-h-48 overflow-auto text-xs">
            <table className="w-full text-left">
              <thead className="text-[10px] uppercase text-slate-500"><tr><th className="py-1">Type</th><th>Kind</th><th>Operation</th><th>Action</th></tr></thead>
              <tbody>
                {report.breakingChanges.changes.slice(0, 40).map((c, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1.5"><StatusPill value={c.type === 'Breaking' ? 'Failed' : c.type === 'Potentially Breaking' ? 'Warning' : 'Passed'} /></td>
                    <td>{c.kind}</td>
                    <td className="font-mono">{c.operation}</td>
                    <td>{c.suggestedAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-sm font-bold">Endpoint coverage</div>
        <div className="grid gap-2 md:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="h-9 w-full rounded-md border border-slate-200 pl-9 pr-3 text-sm" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Search…" />
          </div>
          <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={moduleFilter} onChange={(e) => { setModuleFilter(e.target.value); setPage(1); }}>
            <option value="ALL">All modules</option>
            {modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPage(1); }}>
            <option value="ALL">All methods</option>
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="ALL">All statuses</option>
            <option value="Passed">Contract Passed</option>
            <option value="Failed">Failed / Undocumented</option>
            <option value="Warning">Warning</option>
          </select>
        </div>
        <div className="overflow-auto">
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">Path</th>
                <th className="px-3 py-2">Module</th>
                <th className="px-3 py-2">Docs</th>
                <th className="px-3 py-2">Contract</th>
                <th className="px-3 py-2">Auth</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((e) => (
                <tr key={`${e.method} ${e.path}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-bold">{e.method}</td>
                  <td className="px-3 py-2 font-mono">{e.path}</td>
                  <td className="px-3 py-2">{e.module}</td>
                  <td className="px-3 py-2"><StatusPill value={e.documentationStatus} /></td>
                  <td className="px-3 py-2"><StatusPill value={e.contractStatus} /></td>
                  <td className="px-3 py-2">{e.authentication || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <button type="button" className="rounded border border-slate-200 px-2 py-0.5 font-semibold" onClick={() => navigate('/admin/internal-tools/api-documentation')}>Swagger</button>
                      <button type="button" className="rounded border border-slate-200 px-2 py-0.5 font-semibold" onClick={() => navigate('/admin/internal-tools/api-documentation?panel=playground')}>Playground</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!pageRows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Tidak ada baris</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{filtered.length} endpoints · page {page}/{pageCount}</span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40" onClick={() => setPage((p) => p - 1)}>Prev</button>
            <button type="button" disabled={page >= pageCount} className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40" onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>

      {(report?.coverage?.excluded || []).length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-2 text-sm font-bold">Excluded routes (documented reasons)</div>
          <ul className="space-y-1 text-xs text-slate-600">
            {report.coverage.excluded.map((e) => (
              <li key={`${e.method} ${e.path}`}><span className="font-mono">{e.method} {e.path}</span> — {e.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
